import React, { useState, useEffect } from 'react';
import { Target, Check, Clock, Trophy, Star, Zap, ChevronRight } from 'lucide-react';
import { questsApi } from '../../api';
import { Button } from '../ui/button';
import { Progress } from '../ui/progress';

const QuestBoard = () => {
  const [quests, setQuests] = useState([]);
  const [level, setLevel] = useState(null);
  const [achievements, setAchievements] = useState([]);
  const [activeTab, setActiveTab] = useState('active');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [questsRes, levelRes, achievementsRes] = await Promise.all([
          questsApi.getMyQuests(),
          questsApi.getMyLevel(),
          questsApi.getMyAchievements()
        ]);
        setQuests(questsRes.data);
        setLevel(levelRes.data);
        setAchievements(achievementsRes.data);
      } catch (error) {
        console.error('Failed to fetch quest data:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  const activeQuests = quests.filter(q => q.status === 'in_progress');
  const completedQuests = quests.filter(q => q.status === 'completed' || q.status === 'claimed');

  const getQuestTypeColor = (type) => {
    switch (type) {
      case 'daily': return 'bg-blue-100 text-blue-700';
      case 'weekly': return 'bg-purple-100 text-purple-700';
      case 'monthly': return 'bg-orange-100 text-orange-700';
      case 'special': return 'bg-yellow-100 text-yellow-700';
      case 'onboarding': return 'bg-emerald-100 text-emerald-700';
      default: return 'bg-gray-100 text-gray-700';
    }
  };

  const getDifficultyStars = (difficulty) => {
    return Array(5).fill(0).map((_, i) => (
      <Star
        key={i}
        className={`w-3 h-3 ${i < difficulty ? 'fill-yellow-400 text-yellow-400' : 'text-gray-300'}`}
      />
    ));
  };

  if (loading) {
    return (
      <div className="space-y-6 animate-pulse">
        <div className="h-32 bg-gray-100 rounded-xl" />
        <div className="h-64 bg-gray-100 rounded-xl" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Level Progress */}
      {level && (
        <div className="bg-gradient-to-r from-emerald-500 to-teal-600 rounded-xl p-6 text-white">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              <div className="w-14 h-14 rounded-full bg-white/20 flex items-center justify-center">
                <span className="text-2xl font-bold">{level.level}</span>
              </div>
              <div>
                <p className="text-white/80 text-sm">Current Level</p>
                <p className="text-xl font-semibold">Level {level.level}</p>
              </div>
            </div>
            <div className="text-right">
              <p className="text-white/80 text-sm">Total XP</p>
              <p className="text-xl font-semibold flex items-center gap-1">
                <Zap className="w-5 h-5" />
                {level.total_xp?.toLocaleString()}
              </p>
            </div>
          </div>
          <div>
            <div className="flex justify-between text-sm mb-2">
              <span>{level.current_xp} XP</span>
              <span>{level.xp_to_next_level} XP to Level {level.level + 1}</span>
            </div>
            <div className="h-3 bg-white/20 rounded-full overflow-hidden">
              <div
                className="h-full bg-white rounded-full transition-all"
                style={{ width: `${(level.current_xp / level.xp_to_next_level) * 100}%` }}
              />
            </div>
          </div>
          <div className="flex items-center gap-6 mt-4 text-sm">
            <div className="flex items-center gap-2">
              <Target className="w-4 h-4" />
              <span>{level.quests_completed} Quests</span>
            </div>
            <div className="flex items-center gap-2">
              <Trophy className="w-4 h-4" />
              <span>{level.achievements_earned} Achievements</span>
            </div>
            <div className="flex items-center gap-2">
              <Zap className="w-4 h-4" />
              <span>{level.current_streak} Day Streak</span>
            </div>
          </div>
        </div>
      )}

      {/* Quest Tabs */}
      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
        <div className="flex border-b border-gray-200">
          <button
            onClick={() => setActiveTab('active')}
            className={`flex-1 px-6 py-4 text-sm font-medium transition-colors ${
              activeTab === 'active'
                ? 'bg-emerald-50 text-emerald-600 border-b-2 border-emerald-500'
                : 'text-gray-600 hover:bg-gray-50'
            }`}
          >
            Active Quests ({activeQuests.length})
          </button>
          <button
            onClick={() => setActiveTab('completed')}
            className={`flex-1 px-6 py-4 text-sm font-medium transition-colors ${
              activeTab === 'completed'
                ? 'bg-emerald-50 text-emerald-600 border-b-2 border-emerald-500'
                : 'text-gray-600 hover:bg-gray-50'
            }`}
          >
            Completed ({completedQuests.length})
          </button>
          <button
            onClick={() => setActiveTab('achievements')}
            className={`flex-1 px-6 py-4 text-sm font-medium transition-colors ${
              activeTab === 'achievements'
                ? 'bg-emerald-50 text-emerald-600 border-b-2 border-emerald-500'
                : 'text-gray-600 hover:bg-gray-50'
            }`}
          >
            Achievements ({achievements.length})
          </button>
        </div>

        <div className="p-6">
          {activeTab === 'active' && (
            <div className="space-y-4">
              {activeQuests.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  <Target className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                  <p>No active quests. Check back soon!</p>
                </div>
              ) : (
                activeQuests.map((quest) => (
                  <div
                    key={quest.id}
                    className="flex items-center gap-4 p-4 bg-gray-50 rounded-xl hover:bg-gray-100 transition-colors"
                  >
                    <div className="text-3xl">{quest.icon || '🎯'}</div>
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <h4 className="font-medium text-gray-900">{quest.title}</h4>
                        <span className={`px-2 py-0.5 text-xs font-medium rounded-full ${getQuestTypeColor(quest.type)}`}>
                          {quest.type}
                        </span>
                      </div>
                      <p className="text-sm text-gray-500 mb-2">{quest.description}</p>
                      <div className="flex items-center gap-4">
                        <div className="flex-1">
                          <div className="flex justify-between text-xs text-gray-500 mb-1">
                            <span>{quest.progress}/{quest.target}</span>
                            <span>{Math.round((quest.progress / quest.target) * 100)}%</span>
                          </div>
                          <Progress value={(quest.progress / quest.target) * 100} className="h-2" />
                        </div>
                        <div className="flex items-center gap-1">
                          {getDifficultyStars(quest.difficulty || 1)}
                        </div>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="text-sm font-medium text-emerald-600">+{quest.xp_reward} XP</p>
                      {quest.expires_at && (
                        <p className="text-xs text-gray-400 flex items-center gap-1 mt-1">
                          <Clock className="w-3 h-3" />
                          Expires soon
                        </p>
                      )}
                    </div>
                  </div>
                ))
              )}
            </div>
          )}

          {activeTab === 'completed' && (
            <div className="space-y-4">
              {completedQuests.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  <Check className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                  <p>No completed quests yet. Keep going!</p>
                </div>
              ) : (
                completedQuests.map((quest) => (
                  <div
                    key={quest.id}
                    className="flex items-center gap-4 p-4 bg-emerald-50 rounded-xl opacity-80"
                  >
                    <div className="text-3xl">{quest.icon || '🎯'}</div>
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <h4 className="font-medium text-gray-900">{quest.title}</h4>
                        <Check className="w-4 h-4 text-emerald-500" />
                      </div>
                      <p className="text-sm text-gray-500">{quest.description}</p>
                    </div>
                    <div className="text-emerald-600 font-medium">+{quest.xp_reward} XP</div>
                  </div>
                ))
              )}
            </div>
          )}

          {activeTab === 'achievements' && (
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
              {achievements.length === 0 ? (
                <div className="col-span-full text-center py-8 text-gray-500">
                  <Trophy className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                  <p>No achievements yet. Complete quests to unlock them!</p>
                </div>
              ) : (
                achievements.map((achievement) => (
                  <div
                    key={achievement.id}
                    className="p-4 bg-gradient-to-br from-yellow-50 to-orange-50 rounded-xl text-center border border-yellow-200"
                  >
                    <div className="text-4xl mb-2">{achievement.icon}</div>
                    <h4 className="font-medium text-gray-900">{achievement.name}</h4>
                    <p className="text-xs text-gray-500 mt-1">{achievement.description}</p>
                    <span className={`inline-block mt-2 px-2 py-0.5 text-xs font-medium rounded-full ${
                      achievement.rarity === 'legendary' ? 'bg-yellow-200 text-yellow-800' :
                      achievement.rarity === 'epic' ? 'bg-purple-200 text-purple-800' :
                      achievement.rarity === 'rare' ? 'bg-blue-200 text-blue-800' :
                      'bg-gray-200 text-gray-700'
                    }`}>
                      {achievement.rarity}
                    </span>
                  </div>
                ))
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default QuestBoard;
